import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { DataState } from "../components/ui/DataState";
import {
  REPORT_STATUS_LABELS,
  reportTypeLabel,
  type MyReport,
  type ReportStatus,
} from "../lib/types";

function statusTone(
  status: ReportStatus,
): "neutral" | "warning" | "success" | "critical" | "medium" {
  switch (status) {
    case "approved":
    case "resolved":
      return "success";
    case "rejected":
      return "critical";
    case "more_info_needed":
      return "warning";
    case "needs_verification":
      return "medium";
    default:
      return "neutral";
  }
}

export default function MyReports() {
  const api = useApi();
  const [reports, setReports] = useState<MyReport[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ reports: MyReport[] }>("/api/me/reports")
      .then((r) => {
        setReports(r.reports);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">My reports</h1>
          <p className="mt-2 max-w-2xl text-slate-300">
            Every report you've submitted, with the latest status and reviewer
            note. Anonymous submissions don't appear here — they aren't tied to
            your account by design.
          </p>
        </div>
        <Link to="/submit-report">
          <Button>Submit a new report</Button>
        </Link>
      </header>

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={reports}
          empty={!!reports && reports.length === 0}
          loadingMessage="Loading your reports…"
          emptyMessage="You haven't submitted any reports yet. When you do, status and reviewer feedback will appear here."
        >
          {(items) => (
            <ul className="grid gap-5">
              {items.map((r) => (
                <li key={r.id}>
                  <Card>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={statusTone(r.status)}>
                        {REPORT_STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                      <Badge tone={urgencyTone(r.urgency)}>{r.urgency}</Badge>
                      <Badge>{reportTypeLabel(r.report_type)}</Badge>
                      {r.neighborhood && <Badge>{r.neighborhood}</Badge>}
                    </div>
                    <CardTitle className="mt-3 text-lg">
                      {r.title ?? "Untitled report"}
                    </CardTitle>
                    {r.location && (
                      <p className="mt-1 text-sm text-slate-400">
                        {r.location}
                      </p>
                    )}
                    <dl className="mt-3 grid gap-1 text-xs text-slate-400 sm:grid-cols-2">
                      <div>
                        <dt className="inline text-slate-500">Submitted: </dt>
                        <dd className="inline text-slate-300">
                          {r.created_at}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-slate-500">Last update: </dt>
                        <dd className="inline text-slate-300">
                          {r.updated_at}
                        </dd>
                      </div>
                    </dl>
                    {r.reviewer_note && (
                      <div className="mt-4 rounded-md border border-white/10 bg-white/[0.03] px-4 py-3">
                        <div className="text-xs uppercase tracking-wider text-slate-400">
                          Reviewer note
                          {r.reviewer_note_at && (
                            <span className="ml-2 text-slate-500">
                              · {r.reviewer_note_at}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 whitespace-pre-line text-sm text-slate-200">
                          {r.reviewer_note}
                        </p>
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </DataState>
      </div>
    </div>
  );
}
